import { ApolloQueryResult } from 'apollo-client';
import { forEach, isEqual, isNumber, keys, values } from 'lodash';
import moment from 'moment';

import { UpdateCampaignAvailableForKeyGoalsPayload } from '../../actions/campaignsEdit';
import { InitKeyGoalsEditorsItemData } from '../../actions/metrikaKeyGoals';
import { InitCampaignsRelevantPhrasesEditorsItemData } from '../../actions/relevantPhrases';
import { selectVisitingCardEditorValue } from '../../selectors/visitingCardEditor/visitingCardEditor';
import { getDefaultVCardData } from '../../service/BannerHelpers/BannerHelpers';
import {
    convertToFlatStructureErrors,
    getCampaignEditPageEditorId,
    getCampaignMetrikaKeyGoalsEditorId,
    getEnabledFieldsByType
} from '../../service/CampaignsEditHelpers/CampaignsEditHelpers';
import { castStringToEnum } from '../../service/CastHelpers';
import { parseNumber } from '../../service/FormatHelpers/FormatHelpers';
import { getRandomId } from '../../service/IdHelpers/IdHelpers';
import {
    mapEmailNotificationsType,
    mapNotificationCheckPositionIntervalFromApiFormat,
    mapNotificationCheckPositionIntervalToApiFormat,
    mapSmsNotificationsApiType,
    mapSmsNotificationsType,
    orderEmailNotifications,
    orderSmsNotifications,
    sortNotifications
} from '../../service/NotificationEditorHelper/NotificationEditorHelper';
import {
    formatMinuteTimeToStructuredTime,
    formatStructuredTimeToMinuteTime,
    formatTimeNumberToString
} from '../../service/TimeHelper/TimeHelper';
import {
    getEndHourTimeFromHourIndexForTimeTargeting,
    getHourIndexFromEndHourTimeForTimeTargeting,
    getHoursRangeItemByRange,
    getHoursRangeItemForTimeTargeting,
    getInitialTimeBoard,
    getListSelectedRowForTimeTargetingBoard,
    getSelectedDaysRangeForTimeTargeting,
    getTimeRangeByHoursRangeItem,
    TimeTargetingHoursSelectType
} from '../../service/TimeTargetingEditorHelper/TimeTargetingEditorHelper';
import {
    getVisitingCardMetroStationName,
    isNeedToUpdateEditorVisitingCard
} from '../../service/VisitingCardEditorHelpers/VisitingCardEditorHelpers';
import { MetroStationInfo } from '../../typings/banners';
import {
    CampaignsEditEditorsValidationResult,
    DataForEditCampaigns,
    EditCampaignData,
    EditCampaignEditor,
    EditCampaignsConstants,
    EditCampaignsErrorsData,
    EditCampaignValidationErrors,
    EditCampaignValueField,
    EnabledFieldsMap,
    FetchCampaignsGoalsAvailableForKeyGoalsInput
} from '../../typings/campaignsEdit';
import { ChatListValue } from '../../typings/chatList';
import { ConstData } from '../../typings/constants';
import { DatesIntervalEditorsState } from '../../typings/datesInterval';
import { CampaignPlatform, CampaignType } from '../../typings/domain';
import { AdditionalGoalIds, Goal } from '../../typings/goals';
import {
    CampaignsAvailableForKeyGoalsQuery_client_meaningfulGoals_campaignGoals,
    CampaignsEditDataQuery,
    CampaignsEditDataQuery_client_broadMatchGoals_campaignGoals,
    CampaignsEditDataQuery_client_broadMatchGoals_campaignGoals_goals,
    CampaignsEditDataQuery_client_campaigns_rowset,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_broadMatch,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_flatStrategy,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_flatStrategy_GdCampaignStrategyWeekBundle_budget,
    CampaignsEditDataQuery_client_clientConstants,
    CampaignsEditDataQuery_constants,
    CampaignsEditDataQuery_operator_access,
    GdAddCampaignVcardInput,
    GdAddVcardInput,
    GdAddVcardsInput,
    GdBroadMatchRequestInput,
    GdCampaignBudgetPeriod,
    GdCampaignBudgetShowMode,
    GdCampaignEmailSettingsRequestInput,
    GdCampaignNotificationRequestInput,
    GdCampaignPlatform,
    GdCampaignsAvailableFields,
    GdCampaignSmsEventInput,
    GdContentLanguage,
    GdContentLanguageInput,
    GdDayBudgetShowModeInput,
    GdMeaningfulGoalRequestInput,
    GdUpdateCampaignUnionInput,
    GdUpdateTextCampaignInput,
    RetargetingConditionsQueryForCampaignsEdit_client_retargetingConditions_rowset
} from '../../typings/graphql-grid';
import { MetrikaSelectedKeyGoal } from '../../typings/metrikaKeyGoals';
import {
    CampaignEventsNotification,
    CampaignNotification,
    CampaignsNotificationApiValue,
    ChangeTrafficValueNotification,
    NotificationEditorState,
    NotificationSource,
    NotificationsValue,
    NotificationType,
    PercentOfCurrentBalanceNotification,
    StopByReachDailyBudgetNotification,
    XlsReadyNotification
} from '../../typings/notificationEditor';
import {
    RateControlMode,
    RetargetingConditionDataForRateCorrections
} from '../../typings/ratesCorrections';
import { RelevantPhrasesValue } from '../../typings/relevantPhrases';
import { ClientFeatures, State } from '../../typings/state';
import {
    AttributionModel,
    DnaStrategyType,
    OptimizationCriteria,
    Strategy
} from '../../typings/strategy';
import {
    TimeTargetingEditorHolidaySettings,
    TimeTargetingEditorState,
    TimeTargetingValue,
    TimeTargetingValueHolidaySettings
} from '../../typings/timeTargetingEditor';
import {
    VisitingCardEditorState,
    VisitingCardValueEditCampaign
} from '../../typings/VisitingCardEditor';
import { mapVCardForEditQueryResultToQueryResponse } from '../banners-edit/api-data-mappers';
import {
    mapVCardInfoToApiFormat,
    mapVCardToApiFormat
} from '../banners-edit/state-data-mappers/state-data-mappers';
import {
    getNumberFromApiFormat,
    getStringFromApiFormat,
    getValueFromApiFormat,
    mapCampaignRowTypeToCampaignType,
    mapDateToApiFormat,
    mapMetrikaCountersToApiFormat,
    mapNumberStringToApiFormat
} from '../common/common';
import {
    getAllowedRateCorrectionsDataForCampaign,
    mapRateCorrectionsData,
    mapRatesCorrectionsConstantsFromApiFormat,
    mapRatesCorrectionsValueToApiFormatForCampaign
} from '../rateCorrections/rateCorrections';
import { mapCampaignRelevantPhrasesValueToRelevantPhrasesEditorValue } from '../relevantPhrases';

const experimentsStub = {
    experimentsTree: [
        {
            id: '1',
            name: 'Brand-lift 71492381',
            inner: [
                {
                    id: '2',
                    name: 'A - 70%',
                    inner: null
                },
                {
                    id: '3',
                    name: 'B - 20%',
                    inner: null
                },
                {
                    id: '9',
                    name: 'C - 10%',
                    inner: null
                }
            ]
        },
        {
            id: '4',
            name: 'Yet another experiment',
            inner: [
                {
                    id: '5',
                    name: 'A - 40%',
                    inner: null
                },
                {
                    id: '6',
                    name: 'B - 60%',
                    inner: null
                }
            ]
        },
        {
            id: '7',
            name: 'Collapsed experiment',
            inner: [
                {
                    id: '8',
                    name: 'Never showed segment so far',
                    inner: null
                }
            ]
        },
        {
            id: '10',
            name: 'Experiment - 4',
            inner: [
                {
                    id: '11',
                    name: 'Segment - 100%',
                    inner: null
                }
            ]
        },
        {
            id: '12',
            name: 'Experiment - 5',
            inner: [
                {
                    id: '13',
                    name: 'Segment - 100%',
                    inner: null
                }
            ]
        },
        {
            id: '14',
            name: 'Experiment - 6',
            inner: [
                {
                    id: '15',
                    name: 'Segment - 100%',
                    inner: null
                }
            ]
        }
    ],
    selectedMap: {
        1: true,
        2: true,
        3: true,
        4: true,
        5: true,
        6: true,
        7: true,
        8: true,
        9: true,
        10: true,
        11: true,
        12: true,
        13: true,
        14: false,
        15: true
    },
    areCorrectionsEnabled: false,
    isSegmentTargetingEnabled: false,
    correctionsMap: {
        2: {
            mode: RateControlMode.up,
            value: 0
        },
        3: {
            mode: RateControlMode.down,
            value: 1
        },
        9: {
            mode: RateControlMode.down,
            value: 2
        },
        5: {
            mode: RateControlMode.up,
            value: 0
        },
        6: {
            mode: RateControlMode.down,
            value: 0
        },
        8: {
            mode: RateControlMode.down,
            value: 0
        },
        11: {
            mode: RateControlMode.down,
            value: 0
        },
        13: {
            mode: RateControlMode.down,
            value: 0
        },
        15: {
            mode: RateControlMode.down,
            value: 0
        }
    }
};

export const mapCampaignsForEditQueryResultToQueryResponse = (
    result: ApolloQueryResult<CampaignsEditDataQuery>,
    commonConstants: ConstData,
    metroStations: Record<number, Array<MetroStationInfo>>,
    clientFeatures: ClientFeatures
): DataForEditCampaigns => {
    const {
        client: {
            campaigns,
            clientConstants,
            broadMatchGoals,
            notification: { smsPhone }
        },
        constants
    } = result.data;
    const campaignsRows = campaigns.rowset;
    const allowedRateCorrectionsData = getAllowedRateCorrectionsDataForCampaign(
        clientConstants.allowedBidModifierTypesByCampaignTypes
    );

    return {
        campaigns: campaignsRows.reduce(
            (
                acc: Record<string, EditCampaignData>,
                campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
            ): Record<string, EditCampaignData> => {
                const campaignId = String(campaignRow.id);
                const campaignType = mapCampaignRowTypeToCampaignType(
                    campaignRow.type
                );

                const value = {
                    [EditCampaignValueField.name]: campaignRow.name,
                    [EditCampaignValueField.dates]: {
                        startDate: moment(campaignRow.startDate),
                        endDate: campaignRow.endDate
                            ? moment(campaignRow.endDate)
                            : undefined
                    },
                    [EditCampaignValueField.allowedPageIds]: mapAllowedPageIdsFromApiFormat(
                        campaignRow
                    ),
                    [EditCampaignValueField.disabledIps]: [],
                    [EditCampaignValueField.relevantPhrases]: mapCampaignRelevantPhrasesFromApiFormat(
                        campaignRow,
                        constants.defaultValues.campaign
                            .defaultBroadMatchAllGoalsId
                    ),
                    [EditCampaignValueField.chatOperator]: mapChatOperatorValueFromApi(
                        campaignRow
                    ),
                    [EditCampaignValueField.metrikaCounters]: mapMetrikaCountersFromApiFormat(
                        campaignRow
                    ),
                    // https://st.yandex-team.ru/DIRECT-100605
                    [EditCampaignValueField.experiments]: experimentsStub,
                    // https://st.yandex-team.ru/DIRECT-99943
                    [EditCampaignValueField.metrikaKeyGoals]: {
                        selectedGoals: mapMetrikaKeyGoals(campaignRow)
                    },
                    [EditCampaignValueField.metrikaCounters]: mapMetrikaCounters(
                        campaignRow
                    ),
                    [EditCampaignValueField.markMetrikaLinks]: mapMarkMetrikaLinksFromApiFormat(
                        campaignRow
                    ),
                    [EditCampaignValueField.hasBannersTextSubstitution]: mapHasTitleSubstitutionFromApiFormat(
                        campaignRow
                    ),
                    [EditCampaignValueField.showYandexCatalogInfo]: mapShowYandexCatalogInfoFromApiFormat(
                        campaignRow
                    ),
                    [EditCampaignValueField.considerCompetitors]: mapConsiderCompetitorsFromApiFormat(
                        campaignRow
                    ),
                    [EditCampaignValueField.markTargetCallLinks]: mapMarkTargetCallLinksFromApiFormat(
                        campaignRow
                    ),
                    // DIRECT-97267
                    [EditCampaignValueField.bannersLanguage]: mapBannerLanguage(
                        campaignRow
                    ),
                    [EditCampaignValueField.notifications]: mapCampaignsNotificationFromApiFormat(
                        campaignRow.notification,
                        commonConstants,
                        Boolean(campaignRow.endDate),
                        campaignRow.status.allowDomainMonitoring,
                        getValueFromApiFormat(smsPhone)
                    ),
                    [EditCampaignValueField.strategy]: mapCampaignStrategyFromApiFormat(
                        campaignRow.flatStrategy
                    ),
                    [EditCampaignValueField.bannedDomains]: mapDisabledPlacesToBannedDomains(
                        campaignRow
                    ),
                    [EditCampaignValueField.siteMonitoring]:
                        campaignRow.status.allowDomainMonitoring,
                    [EditCampaignValueField.minusPhrases]: {
                        minusKeywords: [],
                        selectedLibraryList: []
                    },
                    // TODO стаб данных пока нет API
                    [EditCampaignValueField.timeTargeting]: {
                        timeBoard: getInitialTimeBoard(),
                        useWorkingWeekends: true,
                        rateCorrections: undefined,
                        holidaysSettings: {
                            startHour: 8,
                            endHour: 20,
                            rateCorrections: 100,
                            isShow: true
                        },
                        enabledHolidaysMode: false,
                        idTimeZone: '100',
                        idTimeZoneGroup: '1'
                    },
                    [EditCampaignValueField.rateCorrections]: mapRateCorrectionsData(
                        campaignRow.bidModifiers,
                        allowedRateCorrectionsData[campaignType]
                    ),
                    [EditCampaignValueField.hasExtendedGeoTargeting]: mapHasExtendedGeoTargetingFromApiFormat(
                        campaignRow
                    ),
                    [EditCampaignValueField.visitingCard]: mapVisitingCardForEditCampaigns(
                        campaignRow,
                        metroStations
                    )
                };

                acc[campaignId] = {
                    id: campaignId,
                    exportId: String(campaignRow.exportId),
                    type: mapCampaignRowTypeToCampaignType(campaignRow.type),
                    value: value,
                    serverValue: value,
                    isStopped: false,
                    hasNotModeratedBanners: false,
                    areOtherSettingsVisible: false,
                    availableForKeyGoals: {},
                    relevantPhrasesGoals: {
                        isMetrikaAvailable: broadMatchGoals.isMetrikaAvailable,
                        goalsData: mapCampaignBroadMatchGoalsFromApiFormat(
                            broadMatchGoals.campaignGoals,
                            campaignId
                        )
                    },
                    isAvailableForKeyGoalsLoading: true,
                    enabledFields: mapAccessSettingsToEnabledFields(
                        mapCampaignRowTypeToCampaignType(campaignRow.type),
                        result.data.operator.access
                    ),
                    errors: {}
                };

                return acc;
            },
            {}
        ),
        constants: mapEditCampaignConstants(
            constants,
            clientConstants,
            clientFeatures
        )
    };
};

export const mapCampaignBroadMatchGoalsFromApiFormat = (
    broadMatchGoalsData:
        | CampaignsEditDataQuery_client_broadMatchGoals_campaignGoals[]
        | null,
    campaignId: string
): Record<string, Goal> => {
    if (broadMatchGoalsData === null) {
        return {};
    }

    const campaignGoalsInfoFromApi = broadMatchGoalsData.find(item => {
        return String(item.campaignId) === campaignId;
    });

    if (!campaignGoalsInfoFromApi) {
        return {};
    }

    return campaignGoalsInfoFromApi.goals.reduce(
        (
            res: Record<string, Goal>,
            goal: CampaignsEditDataQuery_client_broadMatchGoals_campaignGoals_goals
        ): Record<string, Goal> => {
            res[goal.id] = {
                id: String(goal.id),
                name: goal.name
            };

            return res;
        },
        {}
    );
};

export const mapRetargetingConditionForCampaignsEditFromApiFormat = (
    conditionFromApi: RetargetingConditionsQueryForCampaignsEdit_client_retargetingConditions_rowset
): RetargetingConditionDataForRateCorrections => {
    const { retargetingConditionId, name } = conditionFromApi;

    return {
        id: retargetingConditionId,
        name: name
    };
};

export const mapVisitingCardForEditCampaigns = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset,
    metroStations: Record<number, Array<MetroStationInfo>>
): VisitingCardValueEditCampaign => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        const { commonVcard } = campaignRow;

        const defaultVcard = {
            isShortVersionChecked: false,
            isNotShowMetroInSearchChecked: false,
            info: getDefaultVCardData(),
            vCardId: ''
        };

        if (!commonVcard) {
            return defaultVcard;
        }

        const info = mapVCardForEditQueryResultToQueryResponse(commonVcard);

        const currentMetroStationName = info.metroStationName;

        // ВАЖНО! На бэке хранится тектовое представление станции метро
        // Однако, более точное название (а вдруг), хранится в результатах выполнения ручки на получение всех станций, там данные более актуальные
        // Если удалось вытащить такое имя - при отправке на бэк в мутации отправим уже более точную информацию
        const metroStationName = getVisitingCardMetroStationName(
            info.geoId,
            getValueFromApiFormat(info.metroStationId),
            metroStations
        );

        return {
            ...defaultVcard,
            info: {
                ...info,
                metroStationName: metroStationName
                    ? metroStationName
                    : currentMetroStationName
            },
            vCardId: commonVcard.id ? String(commonVcard.id) : getRandomId(), // для визитки важно иметь id
            isNotShowMetroInSearchChecked: info.metroStationId === '0'
        };
    }

    throw new Error(`Неподдерживаемый тип кампании: ${campaignRow.__typename}`);
};

export const mapBannerLanguage = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): GdContentLanguage | undefined => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
            return getValueFromApiFormat(campaignRow.contentLanguage);
        default:
            return undefined;
    }
};

export const mapAccessSettingsToEnabledFields = (
    campaignType: CampaignType,
    operatorAccess: CampaignsEditDataQuery_operator_access
): EnabledFieldsMap => {
    const enabledFields = getEnabledFieldsByType(campaignType);

    const operatorAccessFieldsDictionary = {
        [GdCampaignsAvailableFields.CONTENT_LANGUAGE]:
            EditCampaignValueField.bannersLanguage,
        [GdCampaignsAvailableFields.ALLOWED_PAGE_IDS]:
            EditCampaignValueField.allowedPageIds
    };

    return Object.keys(operatorAccessFieldsDictionary).reduce(
        (res, accessDependentField) => {
            const field = castStringToEnum(
                accessDependentField,
                GdCampaignsAvailableFields
            );

            return {
                ...res,
                [operatorAccessFieldsDictionary[
                    field
                ]]: operatorAccess.availableFields.campaignFields.includes(
                    field
                )
            };
        },
        enabledFields
    );
};

export const mapMetrikaCounters = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string[] => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
            if (!campaignRow.metrikaCounters) {
                return [];
            }

            return campaignRow.metrikaCounters.map(counter => {
                return String(counter);
            });
        default:
            return [];
    }
};

export const mapEditCampaignConstants = (
    commonConstants: CampaignsEditDataQuery_constants,
    clientConstants: CampaignsEditDataQuery_client_clientConstants,
    clientFeatures: ClientFeatures
): EditCampaignsConstants => {
    const {
        validation: {
            campaignConstants: {
                maxNameLength,
                maxDisabledPlacesCount,
                maxMetrikaCountersNumberForTextCampaign,
                minBroadMatchLimit,
                maxBroadMatchLimit,
                maxDisabledIpsCount
            }
        },
        defaultValues: {
            campaign: { defaultMeaningfulGoalId, defaultBroadMatchAllGoalsId }
        }
    } = commonConstants;

    return {
        maxNameLength,
        involvedSessionsId: defaultMeaningfulGoalId,
        maxBannedDomainsCount: maxDisabledPlacesCount,
        rateCorrectionsConstants: mapRatesCorrectionsConstantsFromApiFormat(
            clientConstants.allowedBidModifiersValues,
            Boolean(clientFeatures.isCpcDeviceModifiersAllowed)
        ),
        allowedRateCorrectionsData: getAllowedRateCorrectionsDataForCampaign(
            clientConstants.allowedBidModifierTypesByCampaignTypes
        ),
        maxMetrikaCountersNumberForTextCampaign,
        maxDisabledIpsCount,
        maxAllowedPageIdsCount: 1000, // перейти на использование константы от бэка, которая будет сделана в DIRECT-103248
        minRelevantPhrasesLimit: minBroadMatchLimit,
        maxRelevantPhrasesLimit: maxBroadMatchLimit,
        defaultRelevantPhrasesAllGoalsId: defaultBroadMatchAllGoalsId
    };
};

export const mapMetrikaKeyGoals = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): MetrikaSelectedKeyGoal[] => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
            const { meaningfulGoals } = campaignRow;
            if (!meaningfulGoals) {
                return [];
            }

            return meaningfulGoals.map(goal => {
                const conversionValue = getValueFromApiFormat(
                    goal.conversionValue
                );
                return {
                    id: goal.goalId,
                    conversionValue:
                        conversionValue === undefined
                            ? ''
                            : String(conversionValue),
                    errors: {
                        goal: [],
                        conversionValue: []
                    }
                };
            });
        default:
            return [];
    }
};

export const mapChatOperatorValueFromApi = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): ChatListValue => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign': {
            return {
                selectedChatId: getStringFromApiFormat(
                    campaignRow.clientDialogId
                )
            };
        }
        default:
            throw new Error(
                `Не поддерживаемый тип кампании: ${campaignRow.__typename}`
            );
    }
};

export const mapDisabledPlacesToBannedDomains = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string[] => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign': {
            return getValueFromApiFormat(campaignRow.disabledPlaces) || [];
        }
        default:
            return [];
    }
};

export const mapShowYandexCatalogInfoFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.enableCompanyInfo;
    }

    return false;
};

export const mapMarkMetrikaLinksFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.hasAddMetrikaTagToUrl;
    }

    return false;
};

export const mapMarkTargetCallLinksFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.hasAddOpenstatTagToUrl;
    }

    return false;
};

export const mapConsiderCompetitorsFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return !campaignRow.excludePausedCompetingAds;
    }

    return false;
};

export const mapHasTitleSubstitutionFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.hasTitleSubstitution;
    }

    return false;
};

export const mapHasExtendedGeoTargetingFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.hasExtendedGeoTargeting;
    }

    return false;
};

export const mapCampaignRelevantPhrasesFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset,
    defaultBroadMatchAllGoalsId: number
): RelevantPhrasesValue => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        const apiFormatData = campaignRow.broadMatch as CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_broadMatch;
        let goalId;

        if (apiFormatData.broadMatchGoalId === null) {
            goalId = AdditionalGoalIds.notSet;
        } else if (
            String(apiFormatData.broadMatchGoalId) ===
            String(defaultBroadMatchAllGoalsId)
        ) {
            goalId = AdditionalGoalIds.all;
        } else {
            goalId = String(apiFormatData.broadMatchGoalId);
        }

        return {
            isEnabled: apiFormatData.broadMatchFlag,
            limit: apiFormatData.broadMatchLimit,
            goalId: goalId
        };
    }

    return {
        isEnabled: false,
        limit: 100,
        goalId: AdditionalGoalIds.all
    };
};

export const mapAllowedPageIdsFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string[] | undefined => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        const allowedPageIds = getValueFromApiFormat(
            campaignRow.allowedPageIds
        );

        return allowedPageIds && allowedPageIds.map(String);
    }

    return undefined;
};

export const mapAllowedPageIdsToApiFormat = (
    allowedPageIds?: string[]
): string[] | null => {
    if (allowedPageIds === undefined) {
        return null;
    }

    return allowedPageIds;
};

export const mapMetrikaCountersFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string[] => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.metrikaCounters
            ? campaignRow.metrikaCounters.reduce((res, counter) => {
                  if (counter === null) {
                      return res;
                  }

                  return [...res, counter.toString()];
              }, [])
            : [];
    }

    return [];
};

export const mapDatesIntervalEditorsDataFromCampaigns = (
    campaignsData: DataForEditCampaigns
): DatesIntervalEditorsState => {
    const { campaigns } = campaignsData;

    return Object.keys(campaigns).reduce((acc, campaignId) => {
        const currentCampaign = campaigns[campaignId];

        const datesValue = currentCampaign.value[EditCampaignValueField.dates];

        if (datesValue && currentCampaign.enabledFields.dates) {
            acc[campaignId] = {
                startDate: datesValue.startDate,
                endDate: datesValue.endDate
            };
        }

        return acc;
    }, {});
};

export const mapCampaignsToFetchAvailableGoalsForKeyInput = (
    campaignsRecord: Record<string, EditCampaignData>
): FetchCampaignsGoalsAvailableForKeyGoalsInput[] => {
    return Object.keys(campaignsRecord).reduce(
        (acc: FetchCampaignsGoalsAvailableForKeyGoalsInput[], campaignId) => {
            const currentCampaign = campaignsRecord[campaignId];
            return [
                ...acc,
                {
                    campaignId: currentCampaign.id,
                    counterIds:
                        currentCampaign.value[
                            EditCampaignValueField.metrikaCounters
                        ]
                }
            ];
        },
        []
    );
};

export const mapCampaignsVisitingCardIds = (
    campaignsRecord: Record<string, EditCampaignData>
): string[] => {
    return Object.keys(campaignsRecord).reduce((acc: string[], campaignId) => {
        const currentCampaign = campaignsRecord[campaignId];
        const visitingCardValue =
            currentCampaign.value[EditCampaignValueField.visitingCard];
        const { vCardId } = visitingCardValue;

        if (vCardId) {
            acc.push(vCardId);
        }

        return acc;
    }, []);
};

export const mapMeaningfulGoalsQueryResultToGoalsAvailableForKeyGoals = (
    campaignGoalsFromApi:
        | CampaignsAvailableForKeyGoalsQuery_client_meaningfulGoals_campaignGoals[]
        | null
): UpdateCampaignAvailableForKeyGoalsPayload[] => {
    if (!campaignGoalsFromApi) {
        return [];
    }

    return campaignGoalsFromApi.map(campaignGoal => {
        return {
            // Будет исправлено на бэке => campaignId: Long!
            // https://st.yandex-team.ru/DIRECT-102460
            campaignId: campaignGoal.campaignId || '',
            goals: campaignGoal.goals.reduce((acc, goal) => {
                return {
                    ...acc,
                    [goal.id]: {
                        id: goal.id,
                        name: goal.name
                    }
                };
            }, {})
        };
    });
};

export const getNotificationsEditorDataFromNotificationsValue = (
    notificationValue: NotificationsValue
): NotificationEditorState => {
    const { smsInterval } = notificationValue;

    return {
        ...notificationValue,
        smsInterval: {
            ...smsInterval,
            inputStartTime: formatTimeNumberToString(smsInterval.startTime),
            inputEndTime: formatTimeNumberToString(smsInterval.endTime)
        },
        isOpen: false,
        isChangedPhone: false
    };
};

export const getNotificationsValueFromNotificationsEditor = (
    notificationEditorData: NotificationEditorState
): NotificationsValue => {
    const { email, phone, smsInterval, notifications } = notificationEditorData;

    return {
        email,
        phone,
        notifications: notifications.map(item => {
            const { errors, ...otherParams } = item;

            return {
                ...otherParams
            };
        }),
        smsInterval: {
            startTime: smsInterval.startTime,
            endTime: smsInterval.endTime
        }
    };
};

export const mapCampaignsNotificationFromApiFormat = (
    notification: CampaignsNotificationApiValue,
    constants: ConstData,
    hasEndDate: boolean,
    siteMonitoring: boolean,
    smsPhone?: string
): NotificationsValue => {
    const {
        defaultValues: {
            campaign: {
                defaultWarningBalance,
                defaultCheckPositionInterval,
                defaultSmsTimeInterval: {
                    startTime: defaultStartTime,
                    endTime: defaultEndTime
                }
            }
        }
    } = constants;
    const {
        smsSettings: {
            smsTime: { endTime = defaultEndTime, startTime = defaultStartTime },
            events: smsEvents
        },
        emailSettings: {
            email,
            allowedEvents: emailAllowedEvents,
            checkPositionInterval,
            sendAccountNews,
            stopByReachDailyBudget,
            warningBalance,
            xlsReady
        }
    } = notification;

    let smsNotifications: CampaignNotification[] = smsEvents.map(itemEvent => {
        const typeSmsEvent: NotificationType =
            mapSmsNotificationsType[itemEvent.event];

        const commonEventData = {
            source: NotificationSource.sms,
            value: itemEvent.checked
        };

        switch (typeSmsEvent) {
            case NotificationType.currentBalance:
                return {
                    ...commonEventData,
                    type: NotificationType.currentBalance,
                    disabled: !smsPhone
                };
            case NotificationType.endCampaign:
                return {
                    ...commonEventData,
                    type: NotificationType.endCampaign,
                    disabled: !hasEndDate || !smsPhone
                };
            case NotificationType.moderationResults:
                return {
                    ...commonEventData,
                    type: NotificationType.moderationResults,
                    disabled: !smsPhone
                };
            case NotificationType.monitoringResults:
                return {
                    ...commonEventData,
                    type: NotificationType.monitoringResults,
                    disabled: !smsPhone || !siteMonitoring
                };
            case NotificationType.increaseBalance:
                return {
                    ...commonEventData,
                    type: NotificationType.increaseBalance,
                    disabled: !smsPhone
                };
            case NotificationType.stopByReachDailyBudget:
                return {
                    ...commonEventData,
                    type: NotificationType.stopByReachDailyBudget,
                    disabled: !smsPhone
                };
            default:
                throw new Error(
                    `Неизвестный тип sms уведомления: ${itemEvent.event}`
                );
        }
    });

    smsNotifications = sortNotifications(
        smsNotifications,
        orderSmsNotifications
    );

    let emailNotifications: CampaignNotification[] = emailAllowedEvents.map(
        allowedEventType => {
            const typeEmailEvent = mapEmailNotificationsType[allowedEventType];

            switch (typeEmailEvent) {
                case NotificationType.changeTrafficValue:
                    const intervalValue =
                        checkPositionInterval || defaultCheckPositionInterval;

                    const changeTrafficValueNotification: ChangeTrafficValueNotification = {
                        source: NotificationSource.email,
                        value: Boolean(checkPositionInterval),
                        type: NotificationType.changeTrafficValue,
                        interval:
                            mapNotificationCheckPositionIntervalFromApiFormat[
                                intervalValue
                            ],
                        disabled: false
                    };

                    return changeTrafficValueNotification;

                case NotificationType.campaignEvents:
                    const campaignEventsNotification: CampaignEventsNotification = {
                        source: NotificationSource.email,
                        value: Boolean(sendAccountNews),
                        type: NotificationType.campaignEvents,
                        disabled: false
                    };

                    return campaignEventsNotification;

                case NotificationType.stopByReachDailyBudget:
                    const stopByReachDailyBudgetNotification: StopByReachDailyBudgetNotification = {
                        source: NotificationSource.email,
                        value: Boolean(stopByReachDailyBudget),
                        type: NotificationType.stopByReachDailyBudget,
                        disabled: false
                    };

                    return stopByReachDailyBudgetNotification;

                case NotificationType.percentOfCurrentBalance:
                    const percentOfCurrentBalanceNotification: PercentOfCurrentBalanceNotification = {
                        source: NotificationSource.email,
                        value: Boolean(warningBalance),
                        type: NotificationType.percentOfCurrentBalance,
                        percent: warningBalance
                            ? String(warningBalance)
                            : String(defaultWarningBalance),
                        disabled: true
                    };

                    return percentOfCurrentBalanceNotification;

                case NotificationType.xlsReady:
                    const xlsReadyNotification: XlsReadyNotification = {
                        source: NotificationSource.email,
                        value: Boolean(xlsReady),
                        type: NotificationType.xlsReady,
                        disabled: false
                    };

                    return xlsReadyNotification;

                default:
                    throw new Error(
                        `Неизвестный тип email уведомления: ${allowedEventType}`
                    );
            }
        }
    );

    emailNotifications = sortNotifications(
        emailNotifications,
        orderEmailNotifications
    );

    return {
        smsInterval: {
            startTime: formatStructuredTimeToMinuteTime(startTime),
            endTime: formatStructuredTimeToMinuteTime(endTime)
        },
        email: email,
        phone: smsPhone,
        notifications: smsNotifications.concat(emailNotifications)
    };
};

export const mapCampaignsNotificationToApiFormat = (
    notificationsValue: NotificationsValue
): GdCampaignNotificationRequestInput => {
    const {
        smsInterval: { startTime, endTime },
        notifications,
        email
    } = notificationsValue;

    const smsNotifications = notifications.filter(
        notification => notification.source === NotificationSource.sms
    );

    const emailNotifications = notifications.filter(
        notification => notification.source === NotificationSource.email
    );

    const emailSettings: GdCampaignEmailSettingsRequestInput = {
        sendAccountNews: null,
        stopByReachDailyBudget: null,
        checkPositionInterval: null,
        xlsReady: null,
        warningBalance: null,
        email
    };

    emailNotifications.forEach(emailNotification => {
        switch (emailNotification.type) {
            case NotificationType.changeTrafficValue:
                const preparedInterval =
                    mapNotificationCheckPositionIntervalToApiFormat[
                        emailNotification.interval
                    ];

                if (preparedInterval !== undefined && emailNotification.value) {
                    emailSettings.checkPositionInterval = preparedInterval;
                }

                break;

            case NotificationType.campaignEvents:
                emailSettings.sendAccountNews = emailNotification.value;

                break;

            case NotificationType.stopByReachDailyBudget:
                emailSettings.stopByReachDailyBudget = emailNotification.value;

                break;

            case NotificationType.percentOfCurrentBalance:
                const preparedPercent =
                    emailNotification.percent &&
                    parseNumber(emailNotification.percent);

                if (isNumber(preparedPercent) && emailNotification.value) {
                    emailSettings.warningBalance = preparedPercent;
                }

                break;

            case NotificationType.xlsReady:
                emailSettings.xlsReady = emailNotification.value;

                break;

            default:
                throw new Error(
                    `Неизвестный тип email уведомления: ${
                        emailNotification.type
                    }`
                );
        }
    });

    return {
        smsSettings: {
            smsTime: {
                startTime: formatMinuteTimeToStructuredTime(startTime),
                endTime: formatMinuteTimeToStructuredTime(endTime)
            },
            enableEvents: smsNotifications.reduce(
                (acc: GdCampaignSmsEventInput[], smsNotification) => {
                    if (smsNotification.value) {
                        acc.push(
                            mapSmsNotificationsApiType[smsNotification.type]
                        );
                    }

                    return acc;
                },
                []
            )
        },
        emailSettings: emailSettings
    };
};

enum serverFieldForCampaignUpdateMutation {
    id = 'id',
    name = 'name',
    startDate = 'startDate',
    endDate = 'endDate',
    notification = 'notification',
    hasTitleSubstitute = 'hasTitleSubstitute',
    biddingStategy = 'biddingStategy',
    metrikaCounters = 'metrikaCounters',
    disabledPlaces = 'disabledPlaces',
    hasSiteMonitoring = 'hasSiteMonitoring',
    contentLanguage = 'contentLanguage',
    dayBudgetShowMode = 'dayBudgetShowMode',
    hasExtendedGeoTargeting = 'hasExtendedGeoTargeting',
    broadMatch = 'broadMatch',
    bidModifiers = 'bidModifiers',
    vcard = 'vcard',
    meaningfulGoals = 'meaningfulGoals',
    clientDialogId = 'clientDialogId',
    disabledIps = 'disabledIps',
    allowedPageIds = 'allowedPageIds',
    hasAddMetrikaTagToUrl = 'hasAddMetrikaTagToUrl',
    hasAddOpenstatTagToUrl = 'hasAddOpenstatTagToUrl',
    excludePausedCompetingAds = 'excludePausedCompetingAds',
    enableCompanyInfo = 'enableCompanyInfo'
}

export const mapTextCampaignDataToApiFormat = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants
): GdUpdateCampaignUnionInput => {
    const campaignId = campaign.id;
    const {
        name,
        dates: { startDate, endDate },
        hasBannersTextSubstitution,
        bannersLanguage,
        bannedDomains,
        notifications,
        siteMonitoring,
        disabledIps,
        chatOperator: { selectedChatId },
        rateCorrections,
        hasExtendedGeoTargeting,
        relevantPhrases,
        allowedPageIds,
        metrikaKeyGoals: { selectedGoals },
        metrikaCounters,
        showYandexCatalogInfo,
        markTargetCallLinks,
        markMetrikaLinks,
        considerCompetitors,
        visitingCard
    } = campaign.value;

    const fieldMapperDict = {
        [serverFieldForCampaignUpdateMutation.name]: name,
        [serverFieldForCampaignUpdateMutation.startDate]: mapDateToApiFormat(
            startDate
        ),
        [serverFieldForCampaignUpdateMutation.endDate]: endDate
            ? mapDateToApiFormat(endDate)
            : null,
        [serverFieldForCampaignUpdateMutation.notification]: mapCampaignsNotificationToApiFormat(
            notifications
        ),
        [serverFieldForCampaignUpdateMutation.hasTitleSubstitute]: hasBannersTextSubstitution,
        [serverFieldForCampaignUpdateMutation.biddingStategy]: {},
        [serverFieldForCampaignUpdateMutation.metrikaCounters]: mapMetrikaCountersToApiFormat(
            metrikaCounters
        ),
        [serverFieldForCampaignUpdateMutation.disabledPlaces]: bannedDomains,
        [serverFieldForCampaignUpdateMutation.hasSiteMonitoring]: siteMonitoring,
        [serverFieldForCampaignUpdateMutation.hasExtendedGeoTargeting]: hasExtendedGeoTargeting,
        [serverFieldForCampaignUpdateMutation.bidModifiers]: mapRatesCorrectionsValueToApiFormatForCampaign(
            rateCorrections,
            campaignId
        ),
        [serverFieldForCampaignUpdateMutation.contentLanguage]: mapBannerLanguageToApiFormat(
            bannersLanguage
        ),
        [serverFieldForCampaignUpdateMutation.meaningfulGoals]: mapMeaningfulGoalsToApiFormat(
            selectedGoals
        ),
        [serverFieldForCampaignUpdateMutation.clientDialogId]: selectedChatId
            ? selectedChatId
            : null,
        [serverFieldForCampaignUpdateMutation.disabledIps]:
            disabledIps.length === 0 ? null : disabledIps,
        [serverFieldForCampaignUpdateMutation.vcard]: mapVCardToApiFormatForEditCampaigns(
            visitingCard
        ),
        [serverFieldForCampaignUpdateMutation.allowedPageIds]: mapAllowedPageIdsToApiFormat(
            allowedPageIds
        ),
        [serverFieldForCampaignUpdateMutation.hasAddMetrikaTagToUrl]: markMetrikaLinks,
        [serverFieldForCampaignUpdateMutation.hasAddOpenstatTagToUrl]: markTargetCallLinks,
        [serverFieldForCampaignUpdateMutation.excludePausedCompetingAds]: !considerCompetitors,
        [serverFieldForCampaignUpdateMutation.enableCompanyInfo]: showYandexCatalogInfo,
        [serverFieldForCampaignUpdateMutation.dayBudgetShowMode]:
            GdDayBudgetShowModeInput.DEFAULT_, /// стаб бекенд бросает ошибку, если не передавать это поле,
        [serverFieldForCampaignUpdateMutation.broadMatch]: mapRelevantPhrasesToApiFormat(
            relevantPhrases,
            constants.defaultRelevantPhrasesAllGoalsId
        )
    };

    const serverFieldToClientField = {
        [serverFieldForCampaignUpdateMutation.name]:
            EditCampaignValueField.name,
        [serverFieldForCampaignUpdateMutation.startDate]:
            EditCampaignValueField.dates,
        [serverFieldForCampaignUpdateMutation.endDate]:
            EditCampaignValueField.dates,
        [serverFieldForCampaignUpdateMutation.notification]:
            EditCampaignValueField.notifications,
        [serverFieldForCampaignUpdateMutation.hasTitleSubstitute]:
            EditCampaignValueField.hasBannersTextSubstitution,
        [serverFieldForCampaignUpdateMutation.biddingStategy]:
            EditCampaignValueField.strategy,
        [serverFieldForCampaignUpdateMutation.dayBudgetShowMode]:
            EditCampaignValueField.strategy,
        [serverFieldForCampaignUpdateMutation.metrikaCounters]:
            EditCampaignValueField.metrikaCounters,
        [serverFieldForCampaignUpdateMutation.disabledPlaces]:
            EditCampaignValueField.bannedDomains,
        [serverFieldForCampaignUpdateMutation.hasSiteMonitoring]:
            EditCampaignValueField.siteMonitoring,
        [serverFieldForCampaignUpdateMutation.contentLanguage]:
            EditCampaignValueField.bannersLanguage,
        [serverFieldForCampaignUpdateMutation.bidModifiers]:
            EditCampaignValueField.rateCorrections,
        [serverFieldForCampaignUpdateMutation.hasExtendedGeoTargeting]:
            EditCampaignValueField.hasExtendedGeoTargeting,
        [serverFieldForCampaignUpdateMutation.broadMatch]:
            EditCampaignValueField.relevantPhrases,
        [serverFieldForCampaignUpdateMutation.meaningfulGoals]:
            EditCampaignValueField.metrikaKeyGoals,
        [serverFieldForCampaignUpdateMutation.clientDialogId]:
            EditCampaignValueField.chatOperator,
        [serverFieldForCampaignUpdateMutation.disabledIps]:
            EditCampaignValueField.disabledIps,
        [serverFieldForCampaignUpdateMutation.allowedPageIds]:
            EditCampaignValueField.allowedPageIds,
        [serverFieldForCampaignUpdateMutation.hasAddMetrikaTagToUrl]:
            EditCampaignValueField.markMetrikaLinks,
        [serverFieldForCampaignUpdateMutation.hasAddOpenstatTagToUrl]:
            EditCampaignValueField.markTargetCallLinks,
        [serverFieldForCampaignUpdateMutation.excludePausedCompetingAds]:
            EditCampaignValueField.considerCompetitors,
        [serverFieldForCampaignUpdateMutation.enableCompanyInfo]:
            EditCampaignValueField.showYandexCatalogInfo,
        [serverFieldForCampaignUpdateMutation.vcard]:
            EditCampaignValueField.visitingCard
    };

    return {
        textCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce(
            (
                res: GdUpdateTextCampaignInput,
                field: serverFieldForCampaignUpdateMutation
            ) => {
                if (campaign.enabledFields[serverFieldToClientField[field]]) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            { id: campaignId } as GdUpdateTextCampaignInput
        )
    };
};

export const mapRelevantPhrasesToApiFormat = (
    value: RelevantPhrasesValue,
    defaultBroadMatchAllGoalsId: number
): GdBroadMatchRequestInput => {
    let goalId;

    if (value.goalId === AdditionalGoalIds.all) {
        goalId = String(defaultBroadMatchAllGoalsId);
    } else if (value.goalId === AdditionalGoalIds.notSet) {
        goalId = null;
    } else {
        goalId = value.goalId;
    }

    return {
        broadMatchFlag: value.isEnabled,
        broadMatchGoalId: goalId,
        broadMatchLimit: value.limit
    };
};

export const mapBannerLanguageToApiFormat = (
    language?: GdContentLanguage
): GdContentLanguageInput | undefined => {
    return language !== undefined
        ? castStringToEnum(String(language), GdContentLanguageInput)
        : undefined;
};

export const mapMeaningfulGoalsToApiFormat = (
    selectedGoals: MetrikaSelectedKeyGoal[]
): GdMeaningfulGoalRequestInput[] => {
    return selectedGoals.map(goal => {
        return {
            goalId: goal.id,
            conversionValue: goal.conversionValue
                ? mapNumberStringToApiFormat(goal.conversionValue)
                : null
        };
    });
};

export const mapCampaignsDataToApiFormat = (
    campaigns: Record<string, EditCampaignData>,
    constants: EditCampaignsConstants
): GdUpdateCampaignUnionInput[] => {
    return values(campaigns).reduce(
        (res: GdUpdateCampaignUnionInput[], campaign: EditCampaignData) => {
            switch (campaign.type) {
                case CampaignType.TEXT:
                    res.push(
                        mapTextCampaignDataToApiFormat(campaign, constants)
                    );
                    break;

                default:
                    break;
            }

            return res;
        },
        []
    );
};

export const mapServerValidationResultForCampaignToEditorsValidationResult = (
    campaignErrors: EditCampaignValidationErrors,
    campaignId: string
): Array<CampaignsEditEditorsValidationResult> => {
    const result: Array<CampaignsEditEditorsValidationResult> = [];

    const errorsBaseData = {
        campaignId
    };

    keys(campaignErrors).forEach(campaignErrorField => {
        switch (campaignErrorField) {
            case EditCampaignValueField.name: {
                const nameErrors = campaignErrors[campaignErrorField];

                if (nameErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.nameEditor,
                        errors: nameErrors.errors || []
                    });
                }

                break;
            }

            case EditCampaignValueField.notifications: {
                const notificationsErrors = campaignErrors[campaignErrorField];

                if (notificationsErrors) {
                    const commonNotificationsErrors =
                        notificationsErrors.common;
                    const preparedCommonNotificationsErrors = keys(
                        commonNotificationsErrors
                    ).reduce((acc, keyField) => {
                        if (commonNotificationsErrors[keyField] !== undefined) {
                            acc[keyField] =
                                commonNotificationsErrors[keyField].errors;
                        }

                        return acc;
                    }, {});

                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.notificationsEditor,
                        errors: {
                            common: {
                                ...preparedCommonNotificationsErrors
                            },
                            notifications: [],
                            isValid: false
                        }
                    });
                }

                break;
            }

            case EditCampaignValueField.metrikaCounters: {
                const metrikaCountersErrors =
                    campaignErrors[campaignErrorField];

                if (metrikaCountersErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.metrikaCounterEditor,
                        errors: metrikaCountersErrors.errors || []
                    });
                }

                break;
            }

            case EditCampaignValueField.bannedDomains: {
                const bannedDomainsErrors = campaignErrors[campaignErrorField];

                result.push({
                    ...errorsBaseData,
                    editor: EditCampaignEditor.bannedDomainsEditor,
                    errors: bannedDomainsErrors.errors
                });

                break;
            }

            case EditCampaignValueField.disabledIps: {
                const disabledIpsErrors = campaignErrors[campaignErrorField];

                result.push({
                    ...errorsBaseData,
                    editor: EditCampaignEditor.disabledIpsEditor,
                    errors: disabledIpsErrors.errors || []
                });
                break;
            }

            case EditCampaignValueField.bannersLanguage: {
                const bannersLanguageErrors =
                    campaignErrors[campaignErrorField];

                if (bannersLanguageErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.bannersLanguage,
                        errors: bannersLanguageErrors.errors || []
                    });
                }

                break;
            }

            case EditCampaignValueField.visitingCard: {
                const visitingCardErrors = campaignErrors[campaignErrorField];

                if (visitingCardErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.visitingCardEditor,
                        errors: convertToFlatStructureErrors(visitingCardErrors)
                    });
                }

                break;
            }

            case 'common': {
                const commonErrors = campaignErrors[campaignErrorField];

                if (commonErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: 'general',
                        errors: commonErrors.errors || []
                    });
                }

                break;
            }

            default:
                break;
        }
    });

    return result;
};

export const mapCampaignsEditServerValidationResultToEditorsValidationResult = (
    updateCampaignsErrorsData: EditCampaignsErrorsData
): Array<CampaignsEditEditorsValidationResult> => {
    let editorsValidationResult: Array<
        CampaignsEditEditorsValidationResult
    > = [];

    forEach(
        updateCampaignsErrorsData.campaigns,
        (campaignErrors: EditCampaignValidationErrors, campaignId: string) => {
            editorsValidationResult = [
                ...editorsValidationResult,
                ...mapServerValidationResultForCampaignToEditorsValidationResult(
                    campaignErrors,
                    campaignId
                )
            ];
        }
    );

    return editorsValidationResult;
};

export const mapCampaignStrategyPlatformFromApiFormat = (
    rawPlatform: GdCampaignPlatform
): CampaignPlatform => {
    switch (rawPlatform) {
        case GdCampaignPlatform.BOTH:
            return CampaignPlatform.BOTH;

        case GdCampaignPlatform.CONTEXT:
            return CampaignPlatform.CONTEXT;

        default:
        case GdCampaignPlatform.SEARCH:
            return CampaignPlatform.SEARCH;
    }
};

export const mapWeeklyBudgetFromApiFormat = (
    rawBudget: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_flatStrategy_GdCampaignStrategyWeekBundle_budget | null
): number | undefined => {
    if (rawBudget && rawBudget.period === GdCampaignBudgetPeriod.WEEK) {
        return getNumberFromApiFormat(rawBudget.sum);
    }

    return;
};

export const mapDailyBudgetFromApiFormat = (
    rawBudget: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_flatStrategy_GdCampaignStrategyWeekBundle_budget | null
): {
    dailyBudgetMode: GdCampaignBudgetShowMode;
    dailyBudget?: number;
} => {
    if (rawBudget && rawBudget.period === GdCampaignBudgetPeriod.DAY) {
        return {
            dailyBudgetMode: rawBudget.showMode,
            dailyBudget: getNumberFromApiFormat(rawBudget.sum)
        };
    }

    return {
        dailyBudgetMode: GdCampaignBudgetShowMode.DEFAULT,
        dailyBudget: undefined
    };
};

export const mapCampaignStrategyFromApiFormat = (
    rawStrategy: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_flatStrategy
): Strategy => {
    const baseData = {
        attributionModel: AttributionModel.LAST_SIGNIFICANT_CLICK, // TODO: Добавить в запрос модель атрибуции DIRECT-99819
        platform: mapCampaignStrategyPlatformFromApiFormat(
            rawStrategy.platform
        ),
        errorCount: 0
    };

    switch (rawStrategy.__typename) {
        case 'GdCampaignStrategyAvgClick':
            return {
                ...baseData,
                type: DnaStrategyType.OPTIMIZE_CLICKS,
                optimizationCriteria: OptimizationCriteria.AVG_CLICK,
                avgClickPriceEnabled: true,
                avgClickPrice: getNumberFromApiFormat(rawStrategy.avgBid),
                weeklyBudgetEnabled:
                    rawStrategy.budget && rawStrategy.budget.sum ? true : false,
                weeklyBudget: mapWeeklyBudgetFromApiFormat(rawStrategy.budget),
                weekBundle: undefined,
                maxClickPrice: undefined,
                errors: {}
            };

        case 'GdCampaignStrategyWeekBundle':
            return {
                ...baseData,
                type: DnaStrategyType.OPTIMIZE_CLICKS,
                optimizationCriteria: OptimizationCriteria.WEEK_BUNDLE,
                avgClickPriceEnabled: Boolean(rawStrategy.avgBid),
                avgClickPrice: getNumberFromApiFormat(rawStrategy.avgBid),
                weeklyBudgetEnabled: false,
                weeklyBudget: undefined,
                weekBundle: getNumberFromApiFormat(rawStrategy.clicksLimit),
                maxClickPrice: !rawStrategy.avgBid
                    ? getNumberFromApiFormat(rawStrategy.bid)
                    : undefined,
                errors: {}
            };

        case 'GdCampaignStrategyWeekBudget': {
            if (rawStrategy.goalId) {
                // если есть цель - то это OPTIMIZE_CPA
                return {
                    ...baseData,
                    type: DnaStrategyType.OPTIMIZE_CPA,
                    goalId: String(rawStrategy.goalId),
                    avgCpaPrice: undefined,
                    maxClickPrice: getNumberFromApiFormat(rawStrategy.bid),
                    weeklyBudget: mapWeeklyBudgetFromApiFormat(
                        rawStrategy.budget
                    ),
                    errors: {}
                };
            }

            return {
                ...baseData,
                type: DnaStrategyType.OPTIMIZE_CLICKS,
                optimizationCriteria: OptimizationCriteria.WEEKLY_BUDGET,
                avgClickPriceEnabled: false,
                avgClickPrice: undefined,
                maxClickPrice: getNumberFromApiFormat(rawStrategy.bid),
                weeklyBudgetEnabled: true,
                weeklyBudget: mapWeeklyBudgetFromApiFormat(rawStrategy.budget),
                weekBundle: undefined,
                errors: {}
            };
        }

        case 'GdCampaignStrategyAvgCpa':
            return {
                ...baseData,
                type: DnaStrategyType.OPTIMIZE_CPA,
                goalId: rawStrategy.goalId
                    ? String(rawStrategy.goalId)
                    : AdditionalGoalIds.all,
                avgCpaPrice: getNumberFromApiFormat(rawStrategy.avgCpa),
                maxClickPrice: getNumberFromApiFormat(rawStrategy.bid),
                weeklyBudget: mapWeeklyBudgetFromApiFormat(rawStrategy.budget),
                errors: {}
            };

        case 'GdCampaignStrategyRoi':
            return {
                ...baseData,
                type: DnaStrategyType.ROI,
                goalId: rawStrategy.goalId
                    ? String(rawStrategy.goalId)
                    : AdditionalGoalIds.all,
                roiCoef: getNumberFromApiFormat(rawStrategy.roiCoef) || 0,
                maxClickPrice: getNumberFromApiFormat(rawStrategy.bid),
                reserveReturn:
                    getNumberFromApiFormat(rawStrategy.reserveReturn) || 0,
                profitability:
                    getNumberFromApiFormat(rawStrategy.profitability) || 0,
                weeklyBudget: mapWeeklyBudgetFromApiFormat(rawStrategy.budget),
                errors: {}
            };

        case 'GdCampaignStrategyManual':
            return {
                ...baseData,
                type: DnaStrategyType.MANUAL,
                ...mapDailyBudgetFromApiFormat(rawStrategy.budget),
                separateBidding: Boolean(rawStrategy.separateBidding),
                contextLimit: 100,
                cpcHoldEnabled: true,
                errors: {}
            };

        default:
            return {
                ...baseData,
                type: DnaStrategyType.MANUAL,
                contextLimit: 100,
                separateBidding: true,
                dailyBudgetMode: GdCampaignBudgetShowMode.DEFAULT,
                dailyBudget: undefined,
                cpcHoldEnabled: false,
                errors: {}
            };
    }
};

export const getTimeTargetingEditorDataFromTimeTargetingValue = (
    value: TimeTargetingValue
): TimeTargetingEditorState => {
    const { timeBoard, holidaysSettings } = value;

    const listSelectedDaysRows = getListSelectedRowForTimeTargetingBoard(
        timeBoard
    );
    const currentHoursRangeItem = getHoursRangeItemForTimeTargeting(timeBoard);
    const currentDaysRangeItem = getSelectedDaysRangeForTimeTargeting(
        listSelectedDaysRows
    );
    const editorHolidaysSettings = getTimeTargetingEditorHolidaySettingsFromValue(
        holidaysSettings
    );

    return {
        ...value,
        selectedDays: listSelectedDaysRows,
        isOpened: true,
        currentDaysRangeItem: currentDaysRangeItem,
        currentHoursRangeItem: currentHoursRangeItem,
        holidaysSettings: editorHolidaysSettings,
        ...getTimeRangeByHoursRangeItem(currentHoursRangeItem)
    };
};

export const getTimeTargetingValueFromTimeTargetingEditor = (
    editor: TimeTargetingEditorState
): TimeTargetingValue => {
    const {
        timeBoard,
        rateCorrections,
        holidaysSettings,
        enabledHolidaysMode,
        useWorkingWeekends,
        idTimeZone,
        idTimeZoneGroup
    } = editor;
    const { currentHoursRangeItem } = holidaysSettings;

    if (currentHoursRangeItem.id === TimeTargetingHoursSelectType.ARBITRARY) {
        throw new Error(
            'пользовательский тип интервала времени не может быть установлен для настроек праздников'
        );
    }

    const valueHolidaysSettings: TimeTargetingValueHolidaySettings = {
        rateCorrections: holidaysSettings.rateCorrections,
        isShow: holidaysSettings.isShow,
        startHour: currentHoursRangeItem.startHour,
        endHour: getEndHourTimeFromHourIndexForTimeTargeting(
            currentHoursRangeItem.endHour
        )
    };

    return {
        timeBoard,
        rateCorrections,
        useWorkingWeekends,
        enabledHolidaysMode,
        idTimeZone,
        idTimeZoneGroup,
        holidaysSettings: valueHolidaysSettings
    };
};

export const getTimeTargetingEditorHolidaySettingsFromValue = (
    holidaysSettings: TimeTargetingValueHolidaySettings
): TimeTargetingEditorHolidaySettings => {
    const { startHour, endHour, ...otherParams } = holidaysSettings;

    const currentHoursRangeItem = getHoursRangeItemByRange({
        startHour: startHour,
        endHour: getHourIndexFromEndHourTimeForTimeTargeting(endHour)
    });

    return {
        currentHoursRangeItem,
        ...otherParams,
        ...getTimeRangeByHoursRangeItem(currentHoursRangeItem)
    };
};

export function mapCampaignsDataForEditToInitKeyGoalsData(
    campaigns: Record<string, EditCampaignData>
): InitKeyGoalsEditorsItemData[] {
    return Object.keys(campaigns).reduce(
        (
            res: InitKeyGoalsEditorsItemData[],
            campaignId: string
        ): InitKeyGoalsEditorsItemData[] => {
            const campaignData = campaigns[campaignId];

            if (
                campaignData.enabledFields[
                    EditCampaignValueField.metrikaKeyGoals
                ]
            ) {
                res.push({
                    editorId: getCampaignMetrikaKeyGoalsEditorId(campaignId),
                    savedValue:
                        campaigns[campaignId].value[
                            EditCampaignValueField.metrikaKeyGoals
                        ].selectedGoals
                });
            }

            return res;
        },
        []
    );
}

export const mapVisitingCardEditCampaignEditorValueToVisitingCardEditCampaignValue = (
    editorValue: VisitingCardEditorState
): VisitingCardValueEditCampaign => {
    const {
        isShortVersionChecked,
        isNotShowMetroInSearchChecked,
        vCardData,
        vCardId
    } = editorValue;

    const hasVCardData =
        vCardData && !isEqual(vCardData, getDefaultVCardData());

    return {
        isShortVersionChecked,
        isNotShowMetroInSearchChecked,
        info: vCardData
            ? {
                  ...vCardData
              }
            : undefined,
        vCardId: hasVCardData ? vCardId : ''
    };
};

export const getVisitingCardRequestData = (
    state: State,
    editorIds: string[],
    campaignIds: string[]
): GdAddVcardsInput => {
    return {
        vcardAddItems: editorIds.reduce(
            (acc: GdAddVcardInput[], editorId, index) => {
                const visitingCardEditorState = selectVisitingCardEditorValue(
                    state,
                    editorId
                );

                const isNeedToUpdate = isNeedToUpdateEditorVisitingCard(
                    visitingCardEditorState
                );

                if (isNeedToUpdate) {
                    const currentVisitingCardValue = mapVisitingCardEditCampaignEditorValueToVisitingCardEditCampaignValue(
                        visitingCardEditorState
                    );

                    acc.push(
                        mapVCardToApiFormat(
                            currentVisitingCardValue,
                            campaignIds[index]
                        )
                    );
                }

                return acc;
            },
            []
        )
    };
};

export function mapCampaignsDataForEditToInitRelevantPhrasesEditors(
    campaigns: Record<string, EditCampaignData>
): InitCampaignsRelevantPhrasesEditorsItemData[] {
    return Object.keys(campaigns).reduce(
        (
            res: InitCampaignsRelevantPhrasesEditorsItemData[],
            campaignId: string
        ): InitCampaignsRelevantPhrasesEditorsItemData[] => {
            const campaignData = campaigns[campaignId];

            if (
                campaignData.enabledFields[
                    EditCampaignValueField.relevantPhrases
                ]
            ) {
                res.push({
                    editorId: getCampaignEditPageEditorId(campaignId),
                    value: mapCampaignRelevantPhrasesValueToRelevantPhrasesEditorValue(
                        campaignData.value[
                            EditCampaignValueField.relevantPhrases
                        ]
                    )
                });
            }

            return res;
        },
        []
    );
}

export const mapVCardToApiFormatForEditCampaigns = (
    vCard: VisitingCardValueEditCampaign
): GdAddCampaignVcardInput | null => {
    const { info, isNotShowMetroInSearchChecked } = vCard;

    if (!info || isEqual(info, getDefaultVCardData())) {
        return null;
    }

    return mapVCardInfoToApiFormat(info, isNotShowMetroInSearchChecked);
};
